import { Box, Text } from "@mantine/core";
import { IconChevronRight } from "@tabler/icons-react";
import CustomBadge from "components/ui/CustomBadge/CustomBadge";
import CustomTable from "components/ui/CustomTable/CustomTable";
import { SALES_ORDER_STATUS_COLOR, SALES_ORDER_STATUS_LABEL } from "constants/common/salesOrder";
import { CollectionReportDetail } from "constants/types/report";
import { MRT_ColumnDef, type MRT_TableOptions } from "mantine-react-table";
import { FC, useMemo } from "react";
import { formatCurrency, formatDate } from "utils/helpers/format";
import PaymentSubTable from "../PaymentSubTable";

interface CollectionCustomTableProps {
  data: CollectionReportDetail[];
}

const CollectionCustomTable: FC<CollectionCustomTableProps> = ({ data }) => {
  const columns = useMemo<MRT_ColumnDef<CollectionReportDetail>[]>(
    () => [
      {
        id: "stt",
        header: "STT",
        size: 50,
        grow: false,
        Cell: ({ row }) => row.index + 1,
        mantineTableHeadCellProps: { align: "center" },
        mantineTableBodyCellProps: { align: "center" },
      },
      { accessorKey: "orderCode", header: "Mã đơn hàng", size: 150 },
      { accessorKey: "partnerName", header: "Khách hàng", size: 200 },
      {
        accessorKey: "contactPerson.fullname",
        header: "Người phụ trách",
        size: 180,
        Cell: ({ row }) => row.original.contactPerson?.fullname ?? "—",
      },
      {
        accessorKey: "orderCreatedAt",
        header: "Ngày tạo",
        size: 150,
        Cell: ({ cell }) => formatDate(cell.getValue<number>()),
      },
      {
        accessorKey: "totalAmount",
        header: "Tổng tiền",
        size: 150,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
      },
      {
        accessorKey: "paidAmount",
        header: "Đã thu",
        size: 140,
        Cell: ({ cell }) => formatCurrency(cell.getValue<number>()),
      },
      {
        accessorKey: "remainingAmount",
        header: "Còn lại",
        size: 140,
        Cell: ({ cell }) => {
          const v = cell.getValue<number>();
          return (
            <Text c={v > 0 ? "orange" : undefined} size="sm">
              {formatCurrency(v)}
            </Text>
          );
        },
      },
      {
        id: "paymentCount",
        header: "Số lần thanh toán",
        size: 150,
        mantineTableHeadCellProps: { align: "center" },
        mantineTableBodyCellProps: { align: "center" },
        Cell: ({ row }) => row.original.payments?.length ?? 0,
      },
      
      {
        accessorKey: "status",
        header: "Trạng thái",
        size: 160,
        grow: false,
        Cell: ({ cell }) => {
          const status = cell.getValue<string>();
          return (
            <CustomBadge
              label={SALES_ORDER_STATUS_LABEL[status] ?? status}
              color={SALES_ORDER_STATUS_COLOR[status] ?? "gray"}
            />
          );
        },
      },
    ],
    [],
  );

  const mrtOptions = useMemo<Partial<MRT_TableOptions<CollectionReportDetail>>>(
    () => ({
      displayColumnDefOptions: {
        "mrt-row-expand": { size: 40, grow: false },
      },
      mantineExpandButtonProps: ({ row }) => ({
        children: (
          <IconChevronRight
            size={16}
            style={{
              transform: row.getIsExpanded() ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 200ms ease",
            }}
          />
        ),
      }),
      renderDetailPanel: ({ row }) => (
        <Box p="sm">
          <PaymentSubTable payments={row.original.payments ?? []} />
        </Box>
      ),
    }),
    [],
  );

  return <CustomTable columns={columns} data={data} mrtOptions={mrtOptions} />;
};

export default CollectionCustomTable;
